import { useState, useEffect, useCallback } from "react";
import { fetchAdminClinics } from "@services/clinicUsers";
import {
  fetchCallLogs,
  fetchMatchingAppointments,
  createMatch,
  removeMatch,
} from "@services/callMatching";

export function useCallMatching() {
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [selectedCallLogId, setSelectedCallLogId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | matched | unmatched
  const [matchModalOpen, setMatchModalOpen] = useState(false);

  // Load UniFi-enabled clinics on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchAdminClinics();
        const all = res?.data || [];
        setClinics(
          all.filter((c) => c.unifi_scraping_enabled && c.is_active !== false),
        );
      } catch {
        setClinics([]);
      }
    })();
  }, []);

  // Load data when clinic changes
  const loadData = useCallback(async (clinicId) => {
    if (!clinicId) {
      setAppointments([]);
      setCallLogs([]);
      return;
    }
    setLoading(true);
    try {
      const [apptRes, clRes] = await Promise.all([
        fetchMatchingAppointments(clinicId),
        fetchCallLogs(clinicId),
      ]);
      setAppointments(apptRes?.data || []);
      setCallLogs(clRes?.data || []);
    } catch {
      setAppointments([]);
      setCallLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClinicId) {
      loadData(selectedClinicId);
    }
  }, [selectedClinicId, loadData]);

  const selectClinic = (clinicId) => {
    setSelectedClinicId(clinicId);
    setSelectedAppointmentId(null);
    setSelectedCallLogId(null);
  };

  const refresh = () => {
    setSelectedAppointmentId(null);
    setSelectedCallLogId(null);
    loadData(selectedClinicId);
  };

  const handleMatch = async () => {
    if (!selectedAppointmentId || !selectedCallLogId) return;
    setActionLoading(true);
    try {
      await createMatch(selectedAppointmentId, selectedCallLogId);
      setSelectedAppointmentId(null);
      setSelectedCallLogId(null);
      setMatchModalOpen(false);
      await loadData(selectedClinicId);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnmatch = async (appointmentId) => {
    setActionLoading(true);
    try {
      await removeMatch(appointmentId);
      await loadData(selectedClinicId);
    } finally {
      setActionLoading(false);
    }
  };

  // Filtered lists
  const filteredAppointments = appointments.filter((a) => {
    if (filter === "matched") return !!a.unifi_call_log_id;
    if (filter === "unmatched") return !a.unifi_call_log_id;
    return true;
  });

  const filteredCallLogs = callLogs.filter((cl) => {
    if (filter === "matched") return !!cl.matched_appointment_id;
    if (filter === "unmatched") return !cl.matched_appointment_id;
    return true;
  });

  return {
    clinics,
    selectedClinicId,
    selectClinic,
    appointments: filteredAppointments,
    callLogs: filteredCallLogs,
    loading,
    actionLoading,
    selectedAppointmentId,
    setSelectedAppointmentId,
    selectedCallLogId,
    setSelectedCallLogId,
    filter,
    setFilter,
    matchModalOpen,
    setMatchModalOpen,
    handleMatch,
    handleUnmatch,
    refresh,
  };
}
